import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Search } from "lucide-react";
import { BOOKING_DIRECTIONS, BOOKING_STATUSES } from "@/lib/constants";
import { ALL } from "./useBookingsData";
import { AuDateInput } from "@/components/AuDateTime";

const SmallLabel = ({ children }) => <label className="block text-xs text-slate-500 mb-1">{children}</label>;

const FilterSelect = ({ label, value, onChange, testId, allLabel, options, span }) => (
  <div className={span}>
    <SmallLabel>{label}</SmallLabel>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const DateFilter = ({ label, value, onChange, testId }) => (
  <div className="md:col-span-2">
    <SmallLabel>{label}</SmallLabel>
    <AuDateInput data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const TogglePill = ({ active, onClick, testId, activeClass, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? activeClass : "bg-white text-slate-600 hover:bg-slate-50"}`}>
    {children}
  </button>
);

const DIRECTION_OPTIONS = BOOKING_DIRECTIONS.map((d) => ({ value: d.value, label: d.value === "inbound" ? "In" : "Out" }));

export default function BookingsFilters({ filters: f, setFilter, sites }) {
  return (
    <Card>
      <CardContent className="py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-2">
          <SmallLabel>Search</SmallLabel>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="bookings-search-input" className="pl-9" placeholder="Ref, sender, receiver, PO, title…"
                   value={f.search} onChange={(e) => setFilter("search")(e.target.value)} />
          </div>
        </div>
        <FilterSelect label="Site" value={f.siteId} onChange={setFilter("siteId")} testId="bookings-site-filter"
                      allLabel="All sites" span="md:col-span-2" options={sites.map((s) => ({ value: s.id, label: s.name }))} />
        <FilterSelect label="Status" value={f.status} onChange={setFilter("status")} testId="bookings-status-filter"
                      allLabel="All statuses" span="md:col-span-2" options={BOOKING_STATUSES} />
        <FilterSelect label="Direction" value={f.direction} onChange={setFilter("direction")} testId="bookings-direction-filter"
                      allLabel="All" span="md:col-span-1" options={DIRECTION_OPTIONS} />
        <DateFilter label="From" value={f.dateFrom} onChange={setFilter("dateFrom")} testId="bookings-date-from" />
        <DateFilter label="To" value={f.dateTo} onChange={setFilter("dateTo")} testId="bookings-date-to" />
        <div className="md:col-span-12 flex flex-wrap gap-2 pt-1">
          <TogglePill active={f.incompleteOnly} onClick={() => setFilter("incompleteOnly")((v) => !v)}
                      testId="filter-incomplete-only" activeClass="bg-amber-100 border-amber-300 text-amber-800">
            <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" />
            Incomplete only
          </TogglePill>
          <TogglePill active={f.onHoldOnly} onClick={() => setFilter("onHoldOnly")((v) => !v)}
                      testId="filter-on-hold-only" activeClass="bg-rose-100 border-rose-300 text-rose-800">
            On hold only
          </TogglePill>
        </div>
      </CardContent>
    </Card>
  );
}
